import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import { supabaseAuth } from '../supabaseClient';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface SummaryChatProps {
  summaryText: string;
  clientName: string;
  clientEmail: string;
  summaryId: string;
  onClose?: () => void;
}

const SummaryChat: React.FC<SummaryChatProps> = ({ 
  summaryText, 
  clientName, 
  clientEmail, 
  summaryId,
  onClose 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStatus, setChatStatus] = useState<{
    can_chat: boolean;
    message_count: number;
    max_messages: number;
    remaining_messages: number;
    limit_reached: boolean;
  } | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check chat status on component mount
  const checkChatStatus = async () => {
    try {
      setIsCheckingStatus(true);
      const { data: { session } } = await supabaseAuth.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${API_BASE_URL}/consultation-forms/summaries/${summaryId}/chat-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        const status = await response.json();
        setChatStatus(status);
        console.log('📊 Chat status:', status);
      } else {
        console.error('❌ Failed to check chat status:', response.statusText);
        // Default to allowing chat if check fails
        setChatStatus({
          can_chat: true,
          message_count: 0,
          max_messages: 3,
          remaining_messages: 3,
          limit_reached: false
        });
      }
    } catch (error) {
      console.error('❌ Error checking chat status:', error);
      // Default to allowing chat if check fails
      setChatStatus({
        can_chat: true,
        message_count: 0,
        max_messages: 3,
        remaining_messages: 3,
        limit_reached: false
      });
    } finally {
      setIsCheckingStatus(false);
    }
  };

  useEffect(() => {
    checkChatStatus();
  }, [summaryId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading || !chatStatus?.can_chat) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Get authentication token
      const { data: { session } } = await supabaseAuth.auth.getSession();
      const token = session?.access_token;

      console.log('🔧 Chat API Debug:', {
        API_BASE_URL,
        fullUrl: `${API_BASE_URL}/ai/chat-with-summary`,
        hasToken: !!token,
        tokenLength: token?.length
      });

      const response = await fetch(`${API_BASE_URL}/ai/chat-with-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: inputValue.trim(),
          summaryText,
          clientName,
          clientEmail,
          summaryId,
        }),
      });

      console.log('📡 Chat API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Chat API Error:', errorText);
        
        // If it's a 429 error (limit reached), update chat status
        if (response.status === 429) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.limit_reached) {
              setChatStatus(prev => ({
                ...prev!,
                can_chat: false,
                limit_reached: true,
                message_count: errorData.message_count || 3,
                max_messages: errorData.max_messages || 3,
                remaining_messages: 0
              }));
            }
          } catch (e) {
            console.error('Failed to parse error response:', e);
          }
        }
        
        throw new Error(`Chat request failed: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      // After successful message, re-check status to update remaining count
      await checkChatStatus();
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if it's a limit reached error
      const isLimitReached = error instanceof Error && 
        error.message.includes('429') && 
        error.message.includes('limit reached');
      
      if (isLimitReached) {
        // For limit reached, add the error message and update status
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: '❌ Chat limit reached for this consultation summary. You have used all 3 questions.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
        
        // Update chat status to reflect limit reached
        setChatStatus(prev => ({
          ...prev!,
          can_chat: false,
          limit_reached: true,
          message_count: 3,
          remaining_messages: 0
        }));
      } else {
        // For other errors (like API failures), don't add to chat history
        // Just show a temporary error state and allow retry
        console.log('🔄 API error occurred, not counting as message - user can retry');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatMessage = (content: string) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="summary-chat">
      <div className="chat-header">
        <div className="chat-title">
          <Bot size={20} />
          <span>Ask about {clientName}'s Assessment</span>
        </div>
        {onClose && (
          <button className="chat-close-btn" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-welcome">
            <div className="welcome-content">
              <Bot size={32} />
              <h3>Ask me anything about this assessment!</h3>
              <p>I can help you understand the immigration analysis, explain specific recommendations, or answer questions about the client's eligibility.</p>
              <div className="suggested-questions">
                <p><strong>Try asking:</strong></p>
                <ul>
                  <li>"What are the main recommendations for this client?"</li>
                  <li>"Explain the CRS score calculation"</li>
                  <li>"What are the next steps for this case?"</li>
                  <li>"How can this client improve their chances?"</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-avatar">
              {message.role === 'user' ? <User size={16} /> : <Bot size={16} />}
            </div>
            <div className="message-content">
              <div 
                className="message-text"
                dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
              />
              <div className="message-time">
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant">
            <div className="message-avatar">
              <Bot size={16} />
            </div>
            <div className="message-content">
              <div className="message-text">
                <Loader2 size={16} className="animate-spin" />
                <span>Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={handleSubmit}>
        {/* Message limit indicator */}
        <div className="message-limit-indicator">
          {isCheckingStatus ? (
            <div className="limit-checking">
              <span className="limit-icon">⏳</span>
              <span className="limit-text">Checking chat availability...</span>
            </div>
          ) : chatStatus?.limit_reached ? (
            <div className="limit-reached">
              <span className="limit-icon">🚫</span>
              <span className="limit-text">
                Chat limit reached ({chatStatus.message_count}/{chatStatus.max_messages})
              </span>
            </div>
          ) : (
            <div className="limit-info">
              <span className="limit-icon">💬</span>
              <span className="limit-text">
                {chatStatus?.remaining_messages || 0} question{(chatStatus?.remaining_messages || 0) !== 1 ? 's' : ''} remaining
              </span>
            </div>
          )}
        </div>
        
        <div className="chat-input-container">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              chatStatus?.limit_reached 
                ? "Chat limit reached for this consultation summary."
                : "💬 Ask anything about this assessment... (e.g., 'What are the next steps?')"
            }
            className="chat-input"
            rows={1}
            disabled={isLoading || !chatStatus?.can_chat || isCheckingStatus}
          />
          <button 
            type="submit" 
            className="chat-send-btn"
            disabled={!inputValue.trim() || isLoading || !chatStatus?.can_chat || isCheckingStatus}
            title={chatStatus?.limit_reached ? "Chat limit reached" : "Send message"}
          >
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default SummaryChat;
